import { formatMoney, useLocale, useTranslation } from '@multaqa/i18n'
import type { WorkflowDocumentType } from './api'
import { useLevelName } from './use-level-name'
import type { WorkflowMode, WorkflowStepDraft } from './workflow-levels'

export function WorkflowSummary({
  companyId,
  document,
  amount,
  currency,
  steps,
  mode = 'chain',
}: {
  companyId: string
  document: WorkflowDocumentType | undefined
  amount: string
  currency: string
  steps: WorkflowStepDraft[]
  mode?: WorkflowMode
}) {
  const { t } = useTranslation('workflow')
  const { locale } = useLocale()
  const levelName = useLevelName(companyId)
  if (!document) return null

  const names = steps.flatMap((step) => {
    const name = levelName(step)
    return name ? [name] : []
  })
  const documentName =
    document.label[locale].charAt(0).toLocaleLowerCase(locale) + document.label[locale].slice(1)

  if (mode === 'escalation') {
    const [first, ...rest] = names
    const summary = first
      ? rest.length > 0
        ? t('form.summary.escalation', {
            document: documentName,
            first,
            rest: rest.join(t('form.summary.separator')),
          })
        : t('form.summary.escalationSingle', { document: documentName, first })
      : t('form.summary.everyDocument', {
          document: documentName,
          approvers: t('form.summary.approverFallback'),
        })
    return <p className="border-s-2 border-brand bg-muted/40 px-3 py-2 text-small">{summary}</p>
  }

  const approverNames = names.length
    ? names.join(t('form.summary.separator'))
    : t('form.summary.approverFallback')
  const article = /^[aeiou]/i.test(documentName) ? 'an' : 'a'
  const summary =
    amount && currency
      ? t('form.summary.threshold', {
          article,
          document: documentName,
          amount: formatMoney(amount, currency, locale),
          approvers: approverNames,
        })
      : t('form.summary.everyDocument', { document: documentName, approvers: approverNames })

  return <p className="border-s-2 border-brand bg-muted/40 px-3 py-2 text-small">{summary}</p>
}
